import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useGetCartQuery,
    useGetStorefrontQuery,
    useUpdateCartBundleMutation,
    useUpdateCartProductMutation,
    useUpdateCartSavedSearchProductMutation,
    useRemoveFromCartMutation,
    useRemoveBundleFromCartMutation
} from '../../Api/Api';
import {
    Image,
    MessageBar,
    MessageBarBody,
    MessageBarActions,
    Button
} from '@fluentui/react-components';
import {
    DismissRegular,
    ArrowCircleUpFilled,
    ArrowCircleDownFilled,
    BoxMultipleSearchFilled,
    EditRegular
} from '@fluentui/react-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../Store/Store';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import NumberInput from '../Helpers/NumberInput';
import TruncateText from '../Helpers/TruncateText';
import CartNotification from './CartNotification';
import EditBundleDrawer from './EditBundleDrawer';
import { IAlternativeProduct, ICartBundle, ICartProduct } from '../../Api/Models/Cart';
import { parseCartPageConfig } from '../Helpers/cartPageConfig';
import { elementStyleFrom, importantStyleRef } from '../Helpers/searchPageConfig';
import { useLocalization } from '../Helpers/useLocalization';

interface ICartItemsViewProps {
    // 'auto' mirrors the full Cart page (desktop table / mobile cards by window width).
    // 'compact' always uses the card list — right for a narrow slide-out panel.
    layout?: 'auto' | 'compact';
    // Overrides the empty-state button action (defaults to navigating home). A panel can
    // instead close itself and go home.
    onEmptyAction?: () => void;
    // Scale of the empty-state icon (the full page uses a huge 24em; a panel wants smaller).
    emptyIconSize?: string;
}

// The cart body — notifications, empty state, and the product/bundle line items — extracted
// from CartPage so the full page and the slide-out cart panel render identically (single source
// of truth). Owns the optimistic quantity state and the update/remove mutations. Styling comes
// from the merchant's cartPageConfig, exactly as the full page.
const CartItemsView: React.FC<ICartItemsViewProps> = (props) => {
    const layout = props.layout ?? 'auto';
    const L = useLocalization();
    const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
    const cart = useGetCartQuery();
    const [updateCart] = useUpdateCartProductMutation();
    const [updateSavedSearchCart] = useUpdateCartSavedSearchProductMutation();
    const [updateCartBundle] = useUpdateCartBundleMutation();
    const [removeFromCart] = useRemoveFromCartMutation();
    const [removeBundleFromCart] = useRemoveBundleFromCartMutation();
    const [updating, setUpdating] = React.useState(false);
    const navigate = useNavigate();

    // The bundle whose configuration is being edited in the slide-out panel (null = closed).
    const [editingBundle, setEditingBundle] = React.useState<ICartBundle | null>(null);
    // Configured selections don't apply to prepared carts, so the edit affordance is hidden there.
    const cartSettings = useSelector((state: RootState) => state.cart);
    const isPreparedCartActive = !!cartSettings.selectedCartId;

    const storefrontSettings = useGetStorefrontQuery();
    const cartConfig = React.useMemo(() => parseCartPageConfig(storefrontSettings.data?.cartPageConfig), [storefrontSettings.data?.cartPageConfig]);
    const emptyStateStyle = React.useMemo(() => elementStyleFrom(cartConfig.emptyState), [cartConfig.emptyState]);
    const emptyStateButtonStyle = React.useMemo(() => elementStyleFrom(cartConfig.emptyStateButton), [cartConfig.emptyStateButton]);
    const rowsContainerStyle = React.useMemo(() => elementStyleFrom(cartConfig.rowsContainer), [cartConfig.rowsContainer]);
    const itemRowRef = React.useMemo(() => { const s = elementStyleFrom(cartConfig.itemRow); return Object.keys(s).length ? importantStyleRef(s) : undefined; }, [cartConfig.itemRow]);
    const bundleRowRef = React.useMemo(() => { const s = elementStyleFrom(cartConfig.bundleRow); return Object.keys(s).length ? importantStyleRef(s) : undefined; }, [cartConfig.bundleRow]);
    // Quantity stepper styling. Most props apply inline, but the per-storefront theme re-skins every
    // .fui-Input background/border with !important — so surface those two as CSS vars that the
    // higher-specificity .fui-Input.cart-qty-input rule in index.css can win with (same trick as the
    // header search). Unset vars leave the theme's default input treatment untouched.
    const quantityInputStyle = React.useMemo(() => elementStyleFrom(cartConfig.quantityInput, 'input'), [cartConfig.quantityInput]);
    const quantityInputVars = React.useMemo(() => {
        const vars: Record<string, string> = {};
        const bg = (quantityInputStyle as React.CSSProperties).backgroundColor as string | undefined;
        const border = (quantityInputStyle as React.CSSProperties).borderColor as string | undefined;
        if (bg) vars['--sf-cart-qty-bg'] = bg;
        if (border) vars['--sf-cart-qty-border'] = border;
        return vars;
    }, [quantityInputStyle]);
    const quantityInputStyleFull = React.useMemo(() => ({ width: '100%', ...quantityInputStyle, ...quantityInputVars }), [quantityInputStyle, quantityInputVars]);

    const [products, setProducts] = React.useState<ICartProduct[]>([]);
    const [bundles, setBundles] = React.useState<ICartBundle[]>([]);
    const [errorProducts, setErrorProducts] = React.useState<string[]>([]);
    const [errorBundles, setErrorBundles] = React.useState<string[]>([]);
    const [dismissedItemNotificationList, setDismissedItemNotificationList] = React.useState<string[]>([]);
    const [removedProducts, setRemovedProducts] = React.useState<string[]>([]);
    const [alternativeProducts, setAlternativeProducts] = React.useState<IAlternativeProduct[]>([]);
    const [removedBundles, setRemovedBundles] = React.useState<string[]>([]);
    const [warning, setWarning] = React.useState<string | null>(null);

    const handleResize = () => setWindowWidth(window.innerWidth);
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    React.useEffect(() => {
        if (cart.data?.products) setProducts(cart.data.products);
        setDismissedItemNotificationList([]);
    }, [cart.data?.products]);
    React.useEffect(() => {
        if (cart.data?.bundles) setBundles(cart.data.bundles);
    }, [cart.data?.bundles]);
    React.useEffect(() => {
        if (cart.data?.removedProducts && cart.data.removedProducts.length > 0) setRemovedProducts(cart.data.removedProducts);
    }, [cart.data?.removedProducts]);
    React.useEffect(() => {
        if (cart.data?.alternativeProducts && cart.data.alternativeProducts.length > 0) setAlternativeProducts(cart.data.alternativeProducts);
    }, [cart.data?.alternativeProducts]);
    React.useEffect(() => {
        if (cart.data?.removedBundles && cart.data.removedBundles.length > 0) setRemovedBundles(cart.data.removedBundles);
    }, [cart.data?.removedBundles]);

    const updateQuantity = async (productId: string, quantity: number) => {
        setErrorProducts([]);
        const relatedProduct = products.find(p => p.productId === productId);
        if (!relatedProduct || relatedProduct?.productId === undefined) return;
        if (relatedProduct.maxQuantity && quantity > relatedProduct.maxQuantity) {
            setErrorProducts((existing) => [...existing, relatedProduct.productId]);
        } else {
            setProducts((prev) => prev.map(product => product.productId === productId ? { ...product, quantity } : product));
            setUpdating(true);
            await updateCart({ productId, quantity, source: 'cart' });
            setUpdating(false);
        }
    };

    const updateQuantitySavedSearch = async (savedSearchId: string, quantity: number) => {
        setErrorProducts([]);
        const relatedProduct = products.find(p => p.savedSearchId === savedSearchId);
        if (!relatedProduct || relatedProduct?.productId === undefined) return;
        if (relatedProduct.maxQuantity && quantity > relatedProduct.maxQuantity) {
            setErrorProducts((existing) => [...existing, relatedProduct.productId]);
        } else {
            setProducts((prev) => prev.map(product => product.savedSearchId === savedSearchId ? { ...product, quantity } : product));
            setUpdating(true);
            await updateSavedSearchCart({ savedSearchId, quantity, source: 'cart' });
            setUpdating(false);
        }
    };

    const updateBundleQuantity = async (cartItemId: string, quantity: number) => {
        setErrorBundles([]);
        const relatedBundle = bundles.find(p => (p.cartItemId ?? p.bundleId) === cartItemId);
        if (!relatedBundle) return;
        if (relatedBundle.maxQuantity && quantity > relatedBundle.maxQuantity) {
            setErrorBundles((existing) => [...existing, cartItemId]);
        } else {
            setUpdating(true);
            setBundles((prev) => prev.map(bundle => (bundle.cartItemId ?? bundle.bundleId) === cartItemId ? { ...bundle, quantity } : bundle));
            await updateCartBundle({ cartItemId, quantity, source: 'cart' })
                .unwrap()
                .then((response) => { if (response.message) setWarning(response.message); });
            setUpdating(false);
        }
    };

    const removeProducts = async (productsToRemove: string[], bundlesToRemove: string[]) => {
        for (const productId of productsToRemove) await removeFromCart({ productId, source: 'cart' });
        for (const cartItemId of bundlesToRemove) await removeBundleFromCart({ cartItemId, source: 'cart' });
    };

    const shouldShowProductChangeNotification = (product: ICartProduct) =>
        cart.data?.alternativeProducts && cart.data.alternativeProducts.find((prod) => prod.savedSearchId !== '' && prod.savedSearchId === product.savedSearchId) && !dismissedItemNotificationList.find(a => a === product.productId);

    const useCompact = layout === 'compact' || windowWidth <= 700;

    const notifications = (
        <>
            {(removedProducts.length > 0 || removedBundles.length > 0) ?
                <CartNotification
                    onDismiss={() => { removeProducts(removedProducts, removedBundles).then(() => { setRemovedProducts([]); setRemovedBundles([]); }); }}
                    message={L('localization_cart_product_removed_notice')}
                /> : <></>}
            {alternativeProducts.length > 0 ?
                <CartNotification
                    onDismiss={() => { setAlternativeProducts([]); }}
                    message={L('localization_cart_products_replaced_notice')}
                /> : <></>}
            {warning ?
                <CartNotification onDismiss={() => { setWarning(null); }} message={warning} /> : <></>}
        </>
    );

    if (products.length === 0 && bundles.length === 0) {
        return (
            <div>
                {notifications}
                <div className={'centered-content'} style={emptyStateStyle}>
                    <BoxMultipleSearchFilled style={{ fontSize: props.emptyIconSize ?? '24em', color: '#ddd', paddingBottom: '20px' }} />
                    <p><h1>{L('localization_cart_empty_heading')}</h1></p>
                    <Button
                        appearance='primary'
                        style={{ width: '25%', marginTop: '1em', maxWidth: '200px', ...emptyStateButtonStyle }}
                        onClick={() => (props.onEmptyAction ? props.onEmptyAction() : navigate('/'))}
                    >
                        {L('localization_common_go_home')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minWidth: 0, flex: 1, ...rowsContainerStyle }}>
            {notifications}
            {!useCompact &&
                <table className='checkout-table'>
                    <thead>
                        <tr>
                            <th style={{ width: '100px' }} />
                            <th>{L('localization_cart_col_product')}</th>
                            <th style={{ width: '150px' }}>{L('localization_cart_col_quantity')}</th>
                            <th style={{ width: '100px', textAlign: 'center' }}>{L('localization_cart_col_price')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((product) => (
                            <React.Fragment key={product.productId}>
                                {shouldShowProductChangeNotification(product) &&
                                    <tr key={`${product.productId}-priceChange`} className='checkout-price-change'>
                                        <td colSpan={4} style={{ padding: 0 }}><div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                            <MessageBar style={{ width: '100%' }}>
                                                <MessageBarBody>
                                                    <div style={{ display: 'flex' }}>{L('localization_cart_product_replaced_row')}</div>
                                                </MessageBarBody>
                                                <MessageBarActions
                                                    containerAction={
                                                        <Button aria-label='dismiss' appearance='transparent' icon={<DismissRegular />}
                                                            onClick={() => setDismissedItemNotificationList((prev) => [...prev, product.productId])} />}
                                                />
                                            </MessageBar>
                                        </div>
                                        </td>
                                            {product.lastPrice && product.price && product.price !== product.lastPrice && !dismissedItemNotificationList.find(a => a === product.productId) &&
                                                <td colSpan={4} style={{ padding: 0 }}>
                                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                                        <MessageBar style={{ width: '100%' }} icon={''}>
                                                            <MessageBarBody>
                                                                <div style={{ display: 'flex' }}>{(product.price > product.lastPrice ?
                                                                    <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                                                                        <div style={{ color: 'red', paddingRight: '5px' }}>
                                                                            <ArrowCircleUpFilled style={{ paddingRight: '5px', fontSize: '32px' }} />
                                                                        </div>
                                                                        <div style={{ fontWeight: 'bold', paddingRight: '5px', alignContent: 'center' }}>{L('localization_cart_price_increased')}</div>
                                                                    </div> :
                                                                    <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                                                                        <div style={{ color: 'green', paddingRight: '5px' }}>
                                                                            <ArrowCircleDownFilled style={{ paddingRight: '5px', fontSize: '32px' }} />
                                                                        </div>
                                                                        <div style={{ fontWeight: 'bold', paddingRight: '5px', alignContent: 'center' }}>{L('localization_cart_price_drop')}</div>
                                                                    </div>)}
                                                                    <div style={{ justifyContent: 'center', alignContent: 'center' }}>{L('localization_cart_price_changed_from')} <b><CurrencyFormat value={product.lastPrice * product.quantity} /></b> {L('localization_cart_price_changed_to')} <b><CurrencyFormat value={product.price * product.quantity} /> </b></div>
                                                                </div>
                                                            </MessageBarBody>
                                                            <MessageBarActions
                                                                containerAction={
                                                                    <Button aria-label='dismiss' appearance='transparent' icon={<DismissRegular />}
                                                                        onClick={() => setDismissedItemNotificationList((prev) => [...prev, product.productId])} />}
                                                            />
                                                        </MessageBar>
                                                    </div>
                                                </td>}
                                        </tr>}
                                <tr key={product.productId} className='checkout-product-row' ref={itemRowRef}>
                                    <td style={{ width: '100px' }}>
                                        <div
                                            onClick={() => { if (product.savedSearchId) { navigate(`/dynamicProducts/${product.savedSearchId}`, { state: { source: 'cart' } }); } else { navigate(`/products/${product.productId}`, { state: { source: 'cart' } }); } }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {product.image && <Image style={{ width: '100px', height: '100px' }} src={product.image} fit='contain' />}
                                        </div>
                                    </td>
                                    <td><h4>{product.name}</h4></td>
                                    <td>
                                        <NumberInput
                                            value={product.quantity}
                                            onChange={async (v) => {
                                                if (v < 0) v = 0;
                                                if (product.savedSearchId) { await updateQuantitySavedSearch(product.savedSearchId, v); } else { await updateQuantity(product.productId, v); }
                                            }}
                                            style={quantityInputStyleFull}
                                            className='cart-qty-input'
                                            includeButtons
                                            disabled={updating || cart.isFetching}
                                        />
                                        {errorProducts.find(a => a === product.productId) &&
                                            <MessageBar intent={'warning'}>
                                                <MessageBarBody>{`${L('localization_cart_limited_to')} ${product.maxQuantity}`}</MessageBarBody>
                                            </MessageBar>}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {product.hidePrice
                                            ? <span className='cart-quote-price'>{L('localization_quote_price_label')}</span>
                                            : product.price && <CurrencyFormat value={product.price * product.quantity} />}
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                        {bundles.map((bundle) => (
                            <tr
                                key={bundle.cartItemId ?? bundle.bundleId}
                                className='checkout-bundle-row'
                                ref={bundleRowRef}
                                data-testid='cart-bundle-row'
                                data-bundle-id={bundle.bundleId}
                                data-unit-price={bundle.price ?? ''}
                                data-quantity={bundle.quantity}
                            >
                                <td style={{ width: '100px' }}>
                                    <div onClick={() => navigate(`/bundles/${bundle.bundleId}`)} style={{ cursor: 'pointer' }}>
                                        {bundle.image && <Image style={{ width: '100px', height: '100px' }} src={bundle.image} fit='contain' />}
                                    </div>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25em' }}>
                                        <h4 style={{ margin: 0 }}>{bundle.name}</h4>
                                        {!isPreparedCartActive &&
                                            <Button
                                                appearance='subtle'
                                                size='small'
                                                aria-label={L('localization_cart_edit_bundle')}
                                                icon={<EditRegular />}
                                                onClick={() => setEditingBundle(bundle)}
                                            />}
                                    </div>
                                    {bundle.products?.map((product) => (
                                        <p key={product.productId} className='cart-bundle-subproduct'>{product.name} x {product.quantity}</p>
                                    ))}
                                </td>
                                <td>
                                    <NumberInput
                                        value={bundle.quantity}
                                        onChange={(v) => updateBundleQuantity(bundle.cartItemId ?? bundle.bundleId, v)}
                                        style={quantityInputStyleFull}
                                        className='cart-qty-input'
                                        includeButtons
                                        disabled={updating || cart.isFetching}
                                    />
                                    {errorBundles.find(a => a === (bundle.cartItemId ?? bundle.bundleId)) &&
                                        <MessageBar intent={'warning'}>
                                            <MessageBarBody>{`${L('localization_cart_limited_to')} ${bundle.maxQuantity}`}</MessageBarBody>
                                        </MessageBar>}
                                </td>
                                <td
                                    style={{ textAlign: 'right' }}
                                    data-testid='cart-bundle-line-total'
                                    data-value={bundle.price ? bundle.price * bundle.quantity : ''}
                                >
                                    {bundle.price && <CurrencyFormat value={bundle.price * bundle.quantity} />}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>}
            {useCompact && (
                <div className='cart-mobile-list'>
                    {products.map((product) => (
                        <div key={product.productId} className='cart-mobile-card' ref={itemRowRef}>
                            <div className='cart-mobile-top'>
                                <div
                                    className='cart-mobile-image'
                                    onClick={() => { if (product.savedSearchId) { navigate(`/dynamicProducts/${product.savedSearchId}`, { state: { source: 'cart' } }); } else { navigate(`/products/${product.productId}`, { state: { source: 'cart' } }); } }}
                                >
                                    {product.image && <Image style={{ width: '75px', height: '75px' }} src={product.image} fit='contain' />}
                                </div>
                                <div className='cart-mobile-name'>
                                    <h4>{product.name && <TruncateText text={product.name} maxLength={50} />}</h4>
                                </div>
                            </div>
                            <div className='cart-mobile-bottom'>
                                <div className='cart-mobile-qty'>
                                    <NumberInput
                                        value={product.quantity}
                                        onChange={async (v) => {
                                            if (v < 0) v = 0;
                                            if (product.savedSearchId) { await updateQuantitySavedSearch(product.savedSearchId, v); } else { await updateQuantity(product.productId, v); }
                                        }}
                                        style={{ fontSize: '16px', ...quantityInputStyleFull }}
                                        className='cart-qty-input'
                                        includeButtons
                                        disabled={updating || cart.isFetching}
                                    />
                                    {errorProducts.find(a => a === product.productId) &&
                                        <MessageBar intent={'warning'}>
                                            <MessageBarBody>{`${L('localization_cart_limited_to')} ${product.maxQuantity}`}</MessageBarBody>
                                        </MessageBar>}
                                </div>
                                <div className='cart-mobile-price'>
                                    {product.hidePrice
                                        ? <span className='cart-quote-price'>{L('localization_quote_price_label')}</span>
                                        : product.price && <CurrencyFormat value={product.price * product.quantity} />}
                                </div>
                            </div>
                        </div>
                    ))}
                    {bundles.map((bundle) => (
                        <div key={bundle.cartItemId ?? bundle.bundleId} className='cart-mobile-card' ref={bundleRowRef}>
                            <div className='cart-mobile-top'>
                                <div className='cart-mobile-image' onClick={() => navigate(`/bundles/${bundle.bundleId}`)}>
                                    {bundle.image && <Image style={{ width: '75px', height: '75px' }} src={bundle.image} fit='contain' />}
                                </div>
                                <div className='cart-mobile-name'>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25em' }}>
                                        <h4 style={{ margin: 0 }}>{bundle.name && <TruncateText text={bundle.name} maxLength={50} />}</h4>
                                        {!isPreparedCartActive &&
                                            <Button
                                                appearance='subtle'
                                                size='small'
                                                aria-label={L('localization_cart_edit_bundle')}
                                                icon={<EditRegular />}
                                                onClick={() => setEditingBundle(bundle)}
                                            />}
                                    </div>
                                    {bundle.products?.map((product) => (
                                        <p key={product.productId} className='cart-mobile-subproduct'>{product.name} x {product.quantity}</p>
                                    ))}
                                </div>
                            </div>
                            <div className='cart-mobile-bottom'>
                                <div className='cart-mobile-qty'>
                                    <NumberInput
                                        value={bundle.quantity}
                                        onChange={(v) => updateBundleQuantity(bundle.cartItemId ?? bundle.bundleId, v)}
                                        style={{ fontSize: '16px', ...quantityInputStyleFull }}
                                        className='cart-qty-input'
                                        includeButtons
                                        disabled={updating || cart.isFetching}
                                    />
                                    {errorBundles.find(a => a === (bundle.cartItemId ?? bundle.bundleId)) &&
                                        <MessageBar intent={'warning'}>
                                            <MessageBarBody>{`${L('localization_cart_limited_to')} ${bundle.maxQuantity}`}</MessageBarBody>
                                        </MessageBar>}
                                </div>
                                <div className='cart-mobile-price'>
                                    {bundle.price && <CurrencyFormat value={bundle.price * bundle.quantity} />}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <EditBundleDrawer
                cartBundle={editingBundle}
                open={!!editingBundle}
                onOpenChange={(open) => { if (!open) setEditingBundle(null); }}
            />
        </div>
    );
};

export default CartItemsView;
